import React, { useState, useCallback, useContext } from 'react';
import { Button } from '@appd/uik';
import DashboardPageTitle from '../DashboardPageTitle';
import { cloneDashboard } from '../../../utils';
import { useRouter } from 'next/router';
import { DashboardJson } from '../../models';
import { DashboardContext } from '../../../hooks';

interface DashboardViewerHeaderProps {
  showPageTitle: boolean;
  dashboardJson: DashboardJson;
  isTenantDashboard: boolean;
  dashboardName: string;
  isEditorMode: boolean;
  useDashifyEditor: boolean;
  useAdvancedDashifyEditor: boolean;
}

const DashboardViewerHeader: React.FC<DashboardViewerHeaderProps> = ({
  showPageTitle,
  dashboardJson,
  isTenantDashboard,
  dashboardName,
  useDashifyEditor,
  isEditorMode,
  useAdvancedDashifyEditor,
}) => {
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const router = useRouter();
  const { addTemplate } = useContext(DashboardContext);

  const onClickDuplicate = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      if (isDuplicating) {
        return;
      }

      setIsDuplicating(true);
      try {
        const clonedDashboard = await cloneDashboard(dashboardJson);
        if (clonedDashboard) {
          addTemplate(clonedDashboard);
          const path = `/${router.query.appName as string}/${encodeURIComponent(clonedDashboard.name ?? '')}/edit`;
          router.push(path).catch(() => {});
        }
      } catch (error) {
        console.error('Error duplicating dashboard:', error);
      } finally {
        setIsDuplicating(false);
      }
    },
    [dashboardJson, router, isDuplicating],
  );

  return (
    <>
      {/* Dashboard Page Title and Navigation  */}
      {showPageTitle && Boolean(dashboardJson?.showPageTitle) && (
        <DashboardPageTitle label={dashboardJson?.title as string} />
      )}
      {/* Editor Actions */}
      {useDashifyEditor && !isEditorMode && !useAdvancedDashifyEditor && (
        <div className="px-9 py-6 flex flex-col gap-1">
          <span className="absolute inline-flex right-5 top-12 ml-auto gap-x-2.5">
            {isTenantDashboard && (
              <Button
                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  e.preventDefault();
                  const path = `/${router.query.appName as string}/${encodeURIComponent(dashboardName)}/edit`;
                  router.push(path).catch(() => {});
                }}
              >
                Edit
              </Button>
            )}
            <Button
              onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClickDuplicate(event)}
              disabled={isDuplicating}
            >
              Duplicate
            </Button>
          </span>
        </div>
      )}
    </>
  );
};
export default DashboardViewerHeader;
